'use client';

import { type ReactNode, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { FilterSettingsMobileFilterDialogButtonV2FragmentFragmentDoc } from '@packages/gql/generated/shopping/FilterSettingsMobileFilterDialogButtonV2FragmentFragmentDoc';

import type { FragmentType } from '@packages/gql/generated/shopping';
import { unmask } from '@packages/gql/src/betterMasking';
import { useAbTesting } from '@packages/shared/src/abtesting/useAbTesting/useAbTesting';

import { FilterSettingsMobileButtonV2 } from '../FilterSettingsMobileButtonV2/FilterSettingsMobileButtonV2';
import { FilterSettingsFabButton } from '../FilterSettingsFabButton/FilterSettingsFabButton';
import { DynamicFilterSettingsMobilePopoversV2 } from '../FilterSettingsMobileV2/FilterSettingsMobilePopoversV2.dynamic';
import { useNestedFilterDialogsV2 } from '../FilterSettingsMobileV2/useNestedFilterDialogsV2';
import { useIsOutOfView } from '../../hooks/useIsOutOfView';
import type { SelectedFilterValue } from '../../types';
import { filtersV2 } from '../../activeFeatureFlags';
import { getTestId } from '../../utils/featureFlags/getTestId';

/* GraphQL */ `
  fragment FilterSettingsMobileFilterDialogButtonV2Fragment on SearchFilterableAndSortableResult {
    id
    ...FilterSettingsMobilePopoversV2Fragment
    ...FilterSettingsMobileButtonV2Fragment
  }
`;

export type FilterSettingsMobileFilterDialogButtonV2Props = {
  maskedData: FragmentType<typeof FilterSettingsMobileFilterDialogButtonV2FragmentFragmentDoc>;
  /** The list of currently selected filter values */
  selectedFilterValues: SelectedFilterValue[];
  /** Callback fired when a user submits the selection of one or more filter values */
  onSubmit?: (filterValues: SelectedFilterValue[]) => void;
  /** If provided, renders the content of the categories dialog, don't forget to invoke the closeDialog callback on selection */
  renderCategoryDialogContent?: (closeDialog: () => void) => ReactNode;
};

const messages = defineMessages({
  filterButton: {
    id: 'filtersV2.productList.mobile.filterButton.text',
    defaultMessage: 'Filtern & Sortieren',
  },
});

export const FilterSettingsMobileFilterDialogButtonV2 = ({
  maskedData,
  selectedFilterValues: initiallySelectedFilterValues,
  onSubmit,
  renderCategoryDialogContent,
}: FilterSettingsMobileFilterDialogButtonV2Props) => {
  const data =
    unmask<typeof FilterSettingsMobileFilterDialogButtonV2FragmentFragmentDoc>(maskedData);

  const { formatMessage } = useIntl();
  const { setOutcome } = useAbTesting();
  const filterButtonRef = useRef<HTMLButtonElement>(null);

  const {
    selectedFilterId,
    activeFilterIds,
    pendingFilterValues,
    hasFilterChanges,
    toggleFilterValue,
    setFilterRange,
    dialogControls,
  } = useNestedFilterDialogsV2({
    initiallySelectedFilterValues,
  });

  const isFabButtonVisible = useIsOutOfView(filterButtonRef);

  const openFilterDialog = () => {
    setOutcome(getTestId(filtersV2), { COF: 1 });
    dialogControls.filterDialog.open();
  };

  return (
    <>
      <div className="w-full">
        <FilterSettingsMobileButtonV2
          ref={filterButtonRef}
          onClick={openFilterDialog}
          maskedData={data}
        >
          {formatMessage(messages.filterButton)}
        </FilterSettingsMobileButtonV2>
      </div>
      <FilterSettingsFabButton visible={isFabButtonVisible} onClick={openFilterDialog} />
      <DynamicFilterSettingsMobilePopoversV2
        requestCacheKey={data.id}
        maskedData={data}
        selectedFilterId={selectedFilterId}
        activeFilterIds={activeFilterIds}
        pendingFilterValues={pendingFilterValues}
        hasFilterChanges={hasFilterChanges}
        dialogControls={dialogControls}
        onToggle={toggleFilterValue}
        onSetRange={setFilterRange}
        onSubmit={onSubmit}
        renderCategoryDialogContent={renderCategoryDialogContent}
      />
    </>
  );
};
